import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import {
  Home as HomeIcon,
  Users,
  Database,
  Search as SearchIcon,
  Sliders,
  Clock,
  Bookmark,
  Radio,
  LogOut,
  Sparkles,
  Tv2,
  ChevronLeft,
  ChevronRight,
  Settings,
  LayoutGrid,
  Wifi,
  Gamepad2,
  Joystick,
} from "lucide-react";
import { Api } from "../lib/api.js";
import { clearBearer } from "../lib/auth.js";

const PRIMARY_NAV = [
  { to: "/",          label: "Home",      Icon: HomeIcon },
  { to: "/remote",    label: "Remote",    Icon: Joystick },
  { to: "/library",   label: "Search",    Icon: SearchIcon },
  { to: "/favorites", label: "Favorites", Icon: Bookmark },
  { to: "/live",      label: "Live",      Icon: Radio },
  { to: "/history",   label: "History",   Icon: Clock },
];

const SETTINGS_NAV: { to: string; label: string; Icon: typeof HomeIcon }[] = [
  { to: "/apps",        label: "Apps",        Icon: LayoutGrid },
  { to: "/profiles",    label: "Profiles",    Icon: Users },
  { to: "/sources",     label: "Sources",     Icon: Database },
  { to: "/providers",   label: "Providers",   Icon: Sparkles },
  { to: "/preferences", label: "Preferences", Icon: Sliders },
  { to: "/wifi",        label: "Wi-Fi",       Icon: Wifi },
  { to: "/remote-pair", label: "Pair Remote", Icon: Gamepad2 },
];

function getCollapsedPref(): boolean {
  return localStorage.getItem("wallace_sidebar_collapsed") === "1";
}

export function Sidebar(): JSX.Element {
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(getCollapsedPref);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const meQ = useQuery({ queryKey: ["auth-me"], queryFn: Api.auth.me, retry: false, staleTime: 60_000 });
  const nowPlayingQ = useQuery({
    queryKey: ["cast-now-playing"],
    queryFn: Api.cast.nowPlaying,
    refetchInterval: 5_000,
    staleTime: 4_000,
  });
  const nowPlaying = nowPlayingQ.data?.active ? nowPlayingQ.data : null;
  const account = meQ.data?.user;

  function toggleCollapsed(): void {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("wallace_sidebar_collapsed", next ? "1" : "0");
    setSettingsOpen(false);
  }

  async function handleLogout(): Promise<void> {
    try { await Api.auth.logout(); } catch { /**/ }
    clearBearer();
    window.location.replace("/login");
  }

  return (
    <aside className={clsx(
      "flex-shrink-0 border-r border-hairline bg-lacquer/80 backdrop-blur-sm flex flex-col min-h-screen transition-[width] duration-200",
      collapsed ? "w-14" : "w-48",
    )}>
      {/* Header */}
      <div className="h-16 flex items-center border-b border-hairline">
        {!collapsed && (
          <div className="flex flex-col leading-tight px-4 flex-1">
            <span className="font-display text-gold text-base tracking-smallcaps">WALLACE</span>
            <span className="font-display text-parchment text-[10px] tracking-smallcaps">— TV —</span>
          </div>
        )}
        <button
          onClick={toggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={clsx(
            "p-2 text-parchment/40 hover:text-ivory transition-colors",
            collapsed ? "mx-auto" : "mr-2",
          )}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>

      {/* Now Playing pill */}
      {nowPlaying && (
        <button
          onClick={() => nav(`/watch/${encodeURIComponent(nowPlaying.item_id!)}`)}
          className={clsx(
            "mx-2 mt-2 mb-1 rounded-md bg-vermilion/15 border border-vermilion/30 hover:bg-vermilion/25 transition-colors text-left flex items-center gap-2",
            collapsed ? "p-2 justify-center" : "px-3 py-2",
          )}
        >
          <Tv2 size={14} className="text-vermilion flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-[9px] text-vermilion/70 uppercase tracking-widest font-display leading-none mb-0.5">Now Playing</div>
              <div className="text-ivory text-xs truncate leading-tight">{nowPlaying.title ?? "Cast in progress"}</div>
            </div>
          )}
          <span className="block w-1.5 h-1.5 rounded-full bg-vermilion flex-shrink-0 animate-pulse" />
        </button>
      )}

      {/* Primary nav */}
      <nav className="px-2 py-3 space-y-0.5 flex-1">
        {PRIMARY_NAV.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              clsx("sidebar-item", isActive && "sidebar-item-active", collapsed && "justify-center px-0")
            }
          >
            <Icon size={16} className="flex-shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {/* Settings items inline when expanded */}
        {!collapsed && (
          <>
            <div className="border-t border-hairline my-2 mx-1" />
            {SETTINGS_NAV.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  clsx("sidebar-item text-parchment/50 text-sm", isActive && "sidebar-item-active")
                }
              >
                <Icon size={15} className="flex-shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-hairline px-2 py-2 space-y-0.5">
        {/* Settings gear — collapsed mode only */}
        {collapsed && (
          <div className="relative">
            <button
              onClick={() => setSettingsOpen(s => !s)}
              title="Settings"
              className={clsx("sidebar-item w-full justify-center px-0", settingsOpen && "sidebar-item-active")}
            >
              <Settings size={16} />
            </button>
            {settingsOpen && (
              <div className="absolute bottom-full left-full mb-1 ml-1 w-44 bg-lacquer border border-hairline rounded-lg shadow-2xl py-1 z-50">
                {SETTINGS_NAV.map(({ to, label, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setSettingsOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        "flex items-center gap-2.5 px-3 py-2 text-sm text-parchment hover:text-ivory hover:bg-elev/40 transition",
                        isActive && "text-ivory",
                      )
                    }
                  >
                    <Icon size={14} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {!collapsed && account && (
          <div className="px-2 py-1 text-[10px]">
            <div className="text-parchment/50 uppercase tracking-smallcaps font-display">Signed in</div>
            <div className="text-ivory/80 truncate" title={account.username}>{account.display_name ?? account.username}</div>
          </div>
        )}

        <button
          onClick={() => void handleLogout()}
          title={collapsed ? "Sign out" : undefined}
          className={clsx("sidebar-item w-full text-left text-parchment/50 hover:text-vermilionHi", collapsed && "justify-center px-0")}
        >
          <LogOut size={15} />
          {!collapsed && <span className="text-sm">Sign out</span>}
        </button>
      </div>
    </aside>
  );
}
